import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {RateBreakdown} from '@findhotel/sapi'
import {getShowTotalPrices} from 'modules/sapiSearch/selectors'
import {getIsMultiRoomBundlesSearch} from 'modules/search/selectors'

import {Grid} from '@vio/atlas/helpers/Grid'
import {SelectionCard} from '@vio/atlas/SelectionCard'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {getNightlyPriceForAllRooms} from '@vio/core/src/price/business/price'
import {getCheckOutDate} from '@vio/core/src/searchParams/validateSearchParamDates'
import {dateStringToMiddayDate} from '@vio/core/src/utils/date'
import {RoundingStrategy} from '@vio/core/src/utils/number'
import {Price} from '@vio/shared/src/search/Price'

interface Props {
  date: string
  currency: string
  price: number
  priceBreakdown: RateBreakdown
  roundingStrategy: RoundingStrategy
  isSelected: boolean
  nights: number
  rooms: number
  dataId: string
  isCheapest: boolean
  onClick: () => void
}

const TileWrapper = styled.div`
  display: flex;
  align-self: stretch;
  flex-shrink: 0;
  min-width: 38%;
  margin-top: ${cssTheme.layout.spacing.s400};
  margin-bottom: ${cssTheme.layout.spacing.s400};

  ${linariaMq.desktopXs} {
    min-width: 140px;
    cursor: pointer;
  }
  ${linariaMq.desktopSm} {
    margin-bottom: ${cssTheme.layout.spacing.s100};
  }
`

const TileContent = styled.div`
  padding-top: ${cssTheme.layout.spacing.s200};
`

const PriceWrapper = styled.div`
  margin-top: ${cssTheme.layout.spacing.s200};
`

const DateWrapper = styled.div`
  text-align: left;
`

export const AvailabilityTile = ({
  date,
  currency,
  price,
  priceBreakdown,
  roundingStrategy,
  isSelected,
  nights,
  dataId,
  isCheapest,
  rooms,
  onClick
}: Props) => {
  const {formatDate, formatMessage} = useIntl()
  const showTotalPrices = useSelector(getShowTotalPrices)
  const isMultiRoomBundlesSearch = useSelector(getIsMultiRoomBundlesSearch)

  const calculatedNightlyPrice = getNightlyPriceForAllRooms(
    price,
    isMultiRoomBundlesSearch,
    rooms
  )

  const calculatedPrice = showTotalPrices
    ? priceBreakdown.base + priceBreakdown.taxes + priceBreakdown.hotelFees
    : calculatedNightlyPrice

  return (
    <TileWrapper>
      <SelectionCard
        dataId={dataId}
        onChange={onClick}
        selected={isSelected}
        name="availability-tile"
        variant="radio"
        withControlButton={false}
        labelText={
          isCheapest
            ? formatMessage({
                id: 'cheapest',
                defaultMessage: 'Cheapest'
              })
            : undefined
        }
      >
        <TileContent>
          <DateWrapper data-id="AvailabilityTileDates">
            <Text variant="bodyS" colorPath="content.neutral.primary">
              {formatDate(dateStringToMiddayDate(date), {
                day: '2-digit',
                month: 'short'
              })}{' '}
              -{' '}
              {formatDate(getCheckOutDate(date, nights), {
                day: '2-digit',
                month: 'short'
              })}
            </Text>
          </DateWrapper>

          <PriceWrapper>
            <Grid container spacing="s200" alignItems="center">
              <Grid mobileSm="fit">
                <Text variant="bodyS" colorPath="content.neutral.primary">
                  <FormattedMessageWrapper id="from" defaultMessage="From" />
                </Text>
              </Grid>
              <Grid mobileSm="fit">
                <Text variant="titleM" colorPath="content.neutral.primary">
                  <Price
                    noStyle
                    currency={currency}
                    value={calculatedPrice}
                    roundingStrategy={roundingStrategy}
                    dataId="AvailabilityTilePrice"
                  />
                </Text>
              </Grid>
            </Grid>
          </PriceWrapper>
        </TileContent>
      </SelectionCard>
    </TileWrapper>
  )
}
